import type { SlideDeckMaterial } from '@breakoutlearning/firebase-repository/models/SlideDeckMaterial'
import { Spinner } from 'components/Spinner'
import { Dialog, InlineDialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { LinkIcon } from 'components/icons/Link'
import type { DocumentData } from 'firebase/firestore'
import { getDownloadURL, ref } from 'firebase/storage'
import { useStorage } from 'hooks/auth'
import { resolveStorageUrl } from 'util/storageUrl'
import { useRootStore } from 'hooks/rootStore'
import { useTranslationTyped } from 'i18n/i18n'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronUp } from 'components/icons/ChevronUp'
import { type VttCue, parseVtt, formatTimestamp } from 'util/vtt'

function clearMediaSessionMetadata() {
  if ('mediaSession' in navigator) {
    navigator.mediaSession.metadata = null
  }
}
function setMediaSessionMetadata(
  slideDeckName: string,
  material: DocumentData,
  imageURL: string | null
) {
  if (!material.data) return
  if ('mediaSession' in navigator) {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: material.data.materialName,
      artist: 'Breakout Learning',
      album: slideDeckName,
      artwork: imageURL
        ? [
            {
              src: imageURL,
              sizes: '512x512',
              type: 'image/png',
            },
          ]
        : [],
    })
  }
}

export const MaterialPlayerDialog = ({
  slideDeckId,
  slideDeckName,
  materialData,
  inline = false,
  onDismiss,
}: {
  slideDeckId: string
  slideDeckName: string
  materialData: Pick<
    SlideDeckMaterial['data'],
    | 'imageUrl'
    | 'materialLink'
    | 'materialName'
    | 'materialDescription'
    | 'vttUrl'
  >
  inline?: boolean
  onDismiss?: () => void
}) => {
  const rootStore = useRootStore()
  const { tt } = useTranslationTyped()
  const [audioURL, setAudioURL] = useState<string | null>(null)
  const [imageURL, setImageURL] = useState<string | null>(null)
  const [cues, setCues] = useState<VttCue[]>([])
  const [activeCueIndex, setActiveCueIndex] = useState(-1)
  const [transcriptOpen, setTranscriptOpen] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)
  const activeCueRef = useRef<HTMLButtonElement>(null)
  const storage = useStorage()

  useEffect(() => {
    return () => {
      clearMediaSessionMetadata()
    }
  }, [])

  useEffect(() => {
    const run = async () => {
      if (!materialData.imageUrl) {
        setImageURL('/assets/images/podcast_card.jpg')
        return
      }
      const imageURL = await resolveStorageUrl(storage, materialData.imageUrl)
      setImageURL(imageURL)
    }
    run()
  }, [storage, slideDeckId, materialData.imageUrl])

  useEffect(() => {
    const run = async () => {
      const audioURL = await resolveStorageUrl(
        storage,
        materialData.materialLink
      )
      setAudioURL(audioURL)
    }
    run()
  }, [storage, slideDeckId, materialData.materialLink])

  // Fetch and parse VTT transcript.
  // Prefer the pre-signed vttUrl from the API (getSlideDeckForAssignment).
  // Fall back to deriving the path from materialLink for contexts where the
  // API doesn't provide a signed VTT URL (e.g. admin/instructor views).
  useEffect(() => {
    if (!materialData.materialLink) return
    if (!/\.mp3(?=(\?|$))/i.test(materialData.materialLink)) return

    const run = async () => {
      try {
        let vttURL: string
        if (materialData.vttUrl) {
          // API provided a pre-signed VTT URL
          vttURL = materialData.vttUrl
        } else if (!materialData.materialLink.includes('X-Goog-Signature=')) {
          // materialLink is a plain storage path — derive VTT path from it
          const vttPath = materialData.materialLink.replace(
            /\.mp3(?=(\?|$))/i,
            '.vtt'
          )
          const vttRef = ref(storage, vttPath)
          vttURL = await getDownloadURL(vttRef)
        } else {
          // materialLink is a signed URL and no vttUrl provided — can't derive
          return
        }
        const response = await fetch(vttURL)
        if (!response.ok) return
        const contentType = response.headers.get('content-type') ?? ''
        if (!contentType.includes('text')) return
        const text = await response.text()
        const parsed = parseVtt(text)
        if (parsed.length > 0) setCues(parsed)
      } catch {
        // VTT may not exist — silently ignore
      }
    }
    run()
  }, [storage, materialData.materialLink, materialData.vttUrl])

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current || cues.length === 0) return
    const t = audioRef.current.currentTime
    const idx = cues.findIndex((cue) => t >= cue.startTime && t < cue.endTime)
    setActiveCueIndex(idx)
  }, [cues])

  // Auto-scroll to active cue
  useEffect(() => {
    if (activeCueRef.current) {
      activeCueRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [activeCueIndex])

  const seekToCue = useCallback((startTime: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = startTime
      audioRef.current.play()
    }
  }, [])

  const DialogToUse = inline ? InlineDialog : Dialog

  if (!imageURL || !audioURL)
    return (
      <DialogToUse
        size="xs"
        aria-labelledby={undefined}
        aria-label={tt.material.view_file()}
      >
        <DialogCloseButton
          onClick={() => {
            if (onDismiss) onDismiss()
            return !inline
          }}
        />
        <div className="flex min-h-[300px] w-full items-center justify-center">
          <Spinner />
        </div>
      </DialogToUse>
    )

  return (
    <DialogToUse
      size="xs"
      className="!bg-core-tertiary"
      onDismiss={onDismiss}
      aria-labelledby={undefined}
      aria-label={tt.material.view_file()}
    >
      <DialogCloseButton
        onClick={() => {
          if (onDismiss) onDismiss()
          return !inline
        }}
      />
      <div className="box-border max-h-[80vh]">
        {imageURL && (
          <img
            src={imageURL}
            className="mx-auto max-h-[30vh] rounded-2xl"
            alt=""
          />
        )}
        <h1 className="mt-4 font-bold">{materialData.materialName}</h1>
        <p>{materialData.materialDescription}</p>

        {audioURL && (
          <audio
            ref={audioRef}
            controls
            onPlay={() =>
              setMediaSessionMetadata(slideDeckName, materialData, imageURL)
            }
            onTimeUpdate={handleTimeUpdate}
            className="podcast-audio mt-4 w-full"
            controlsList="nodownload nofullscreen noremoteplayback"
          >
            <source src={audioURL} />
          </audio>
        )}

        {cues.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setTranscriptOpen((prev) => !prev)}
              aria-expanded={transcriptOpen}
              className="flex w-full cursor-pointer items-center justify-between text-label-medium"
            >
              <span>
                {transcriptOpen
                  ? tt.material.hide_transcript()
                  : tt.material.show_transcript()}
              </span>
              <ChevronUp
                size={18}
                className={`transition-transform ${transcriptOpen ? '' : 'rotate-180'}`}
              />
            </button>
            {transcriptOpen && (
              <div className="mt-2 max-h-[30vh] overflow-y-auto rounded-xl bg-surface p-3">
                {cues.map((cue, i) => (
                  <button
                    key={i}
                    ref={i === activeCueIndex ? activeCueRef : undefined}
                    onClick={() => seekToCue(cue.startTime)}
                    className={`flex w-full cursor-pointer gap-2 rounded-lg px-2 py-1.5 text-left text-body-small transition-colors ${
                      i === activeCueIndex
                        ? 'bg-fixed-accent-color text-core-on-secondary'
                        : 'hover:bg-surface-bright'
                    }`}
                  >
                    <span className="shrink-0 font-mono text-xs opacity-60">
                      {formatTimestamp(cue.startTime)}
                    </span>
                    <span>{cue.text}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <a
          href="#"
          onClick={() => {
            rootStore.navigateToPodcastsPage({
              returnTo: window.location.pathname + window.location.search,
            })
          }}
          className="mt-5 flex cursor-pointer flex-row items-center gap-2 text-nowrap rounded-2xl bg-surface p-3 text-body-medium"
        >
          <LinkIcon size={14} />
          {tt.onboarding.link_podcast_app()}
        </a>
      </div>
    </DialogToUse>
  )
}
